/**
 * Перенос `lib/src/utils/sse/sse_event.dart`.
 *
 * Dart-класс с `static parse` перенесён как `interface` + свободные функции:
 * данные события неизменяемы, поведение разбора вынесено в `parseSseEvent`.
 */

/** Одно событие SSE-потока. */
export interface SseEvent {
  /** Сырой текст события — для лога, когда разбор не удался. */
  readonly raw: string;

  readonly id: string | null;
  readonly event: string | null;

  /**
   * Разобранный JSON из поля data. null, если data не было
   * или это оказался не JSON.
   */
  readonly data: unknown;

  /**
   * Было ли в блоке поле `data` вообще.
   *
   * По одному `data === null` комментарий keep-alive (`: ping`) не
   * отличить от события с испорченным JSON, а разница важна: первое —
   * обычный пульс соединения, второе — признак того, что сервис сменил
   * формат. Блок из одних комментариев Dart тоже отдавал в `onEvent`,
   * поэтому фильтровать его приходится на стороне потребителя.
   */
  readonly hasData: boolean;
}

/** `SseEvent.dataAsMap` — data, только если это JSON-объект. */
export function sseEventDataAsMap(event: SseEvent): Record<string, unknown> | null {
  const value = event.data;
  const isMap = typeof value === 'object' && value !== null && !Array.isArray(value);
  return isMap ? (value as Record<string, unknown>) : null;
}

/**
 * Аналог `const LineSplitter().convert(block)` из `dart:convert`.
 *
 * Делит по `\r\n`, `\r` и `\n`; завершающий перевод строки не даёт лишнего
 * пустого элемента — как и в Dart (`'a\n'` → `['a']`, `''` → `[]`).
 */
function splitLines(block: string): readonly string[] {
  const lines = block.split(/\r\n|\r|\n/);
  return lines.length > 0 && lines[lines.length - 1] === '' ? lines.slice(0, -1) : lines;
}

interface SseFields {
  readonly id: string | null;
  readonly event: string | null;
  readonly dataLines: readonly string[];
}

const EMPTY_FIELDS: SseFields = { id: null, event: null, dataLines: [] };

/**
 * Одна строка блока: `field: value`. Двоеточие ищется только первое —
 * в значении оно встречается сплошь и рядом (`data: {"url":"http://…"}`).
 */
function applyLine(fields: SseFields, line: string): SseFields {
  const colon = line.indexOf(':');
  const field = colon === -1 ? line : line.substring(0, colon);
  const value = colon === -1 ? '' : line.substring(colon + 1).trimStart();

  switch (field) {
    case 'id':
      return { ...fields, id: value };
    case 'event':
      return { ...fields, event: value };
    case 'data':
      return { ...fields, dataLines: [...fields.dataLines, value] };
    default:
      return fields;
  }
}

/**
 * Разбирает накопленный блок строк между пустыми строками.
 *
 * По спецификации SSE поле data может повторяться — тогда строки
 * склеиваются через перевод строки.
 */
export function parseSseEvent(block: string): SseEvent {
  let fields = EMPTY_FIELDS;

  for (const line of splitLines(block)) {
    // Пустая строка — разделитель, строка с ':' в начале — комментарий (keep-alive).
    if (line.length === 0 || line.startsWith(':')) continue;
    fields = applyLine(fields, line);
  }

  let data: unknown = null;
  if (fields.dataLines.length > 0) {
    const joined = fields.dataLines.join('\n');
    try {
      data = JSON.parse(joined);
    } catch {
      // Не JSON — не ошибка: сырой текст остаётся в `raw` для лога.
      data = null;
    }
  }

  return {
    raw: block,
    id: fields.id,
    event: fields.event,
    data,
    hasData: fields.dataLines.length > 0,
  };
}
